/**
 * TypeConfig - 项目中的类型和枚举值统一管理
 * 
 * 这个文件集中定义了项目中使用的所有枚举值、常量和类型定义，
 * 避免在代码中散布魔法数字，提高代码的可维护性和可读性。
 */

/**
 * 图标类型枚举
 * 用于表示网站/应用的图标显示偏好
 */
export const PREFERRED_ICON_TYPE = {
  /** 使用网站ico图标背景 */
  ICO: '1',
  /** 使用文本图标背景 */
  TEXT: '2',
  /** 使用表情符号图标 */
  EMOJI: '3'
};

/**
 * 图标类型描述映射
 * 用于调试和日志输出时的可读性
 */
export const PREFERRED_ICON_TYPE_LABELS = {
  [PREFERRED_ICON_TYPE.ICO]: 'ICO图标',
  [PREFERRED_ICON_TYPE.TEXT]: '文本图标',
  [PREFERRED_ICON_TYPE.EMOJI]: '表情符号'
};

/**
 * 拖拽状态枚举
 * 用于表示拖拽操作的不同状态
 */
export const DRAG_STATE = {
  /** 未拖拽 */
  NONE: 0,
  /** 拖拽中 */
  DRAGGING: 1,
  /** 拖拽悬停 */
  DRAG_OVER: 2,
  /** 拖拽完成 */
  DROPPED: 3
};

/**
 * 应用操作类型枚举
 * 用于表示对应用的不同操作类型
 */
export const APP_ACTION_TYPE = {
  /** 添加应用 */
  ADD: 'add',
  /** 编辑应用 */
  EDIT: 'edit',
  /** 删除应用 */
  DELETE: 'delete',
  /** 移动应用 */
  MOVE: 'move',
  /** 拖拽应用 */
  DRAG: 'drag'
};

/**
 * 数据类型枚举
 * 用于表示不同的数据内容类型
 */
export const DATA_TYPE = {
  /** 网站导航数据 */
  NAVIGATION: 0,
  /** 热搜卡片数据 */
  HOT_SEARCH: 1,
  /** 每日英语卡片数据 */
  DAILY_ENGLISH: 2
};

/**
 * 数据类型描述映射
 * 用于调试和日志输出时的可读性
 */
export const DATA_TYPE_LABELS = {
  [DATA_TYPE.NAVIGATION]: '网站导航',
  [DATA_TYPE.HOT_SEARCH]: '热搜卡片',
  [DATA_TYPE.DAILY_ENGLISH]: '每日英语卡片'
};

/**
 * 存储键名枚举
 * 用于LocalStorage中的键名管理
 */
export const STORAGE_KEYS = {
  /** 认证相关 */
  AUTH: {
    TOKEN: 'authToken',
    USER_INFO: 'userInfo',
    DATA_SYNCED: 'dataSynced',
    SYNC_IN_PROGRESS: 'syncInProgress'
  },
  /** 导航相关 */
  NAV: {
    LAST_SELECTED: 'lastSelectedNav',
    LAST_CATEGORY: 'lastSelectedCategory',
    ITEMS: 'sideNavItems'
  },
  /** Dock相关 */
  DOCK: {
    RECENT_APPS: 'dockRecentApps',
    PINNED_APPS: 'dockPinnedApps'
  },
  /** 设置相关 */
  SETTINGS: {
    WALLPAPER: 'selectedWallpaper',
    SEARCH_ENGINE: 'searchEngine',
    ENABLE_LOGS: 'enableLogs',
    START_PAGE_PROMPT: 'hasPromptedStartPage',
    AUTO_FOCUS_SEARCH: 'autoFocusSearch',
    SHOW_SEARCH: 'showSearch',
    SHOW_QUICK_SEARCH: 'showQuickSearch'
  },
  /** 网站数据相关 */
  WEBSITES: {
    HOME: 'homeWebsites',
    PREFIX: 'navWebsites_'
  }
};

/**
 * 事件类型枚举
 * 用于自定义事件的类型管理
 */
export const EVENT_TYPES = {
  /** 网站点击事件 */
  WEBSITE_CLICK: 'websiteClick',
  /** 网站删除事件 */
  WEBSITE_DELETE: 'websiteDelete',
  /** 图标加载错误事件 */
  ICON_LOAD_ERROR: 'iconLoadError',
  /** 拖拽事件 */
  CUSTOM_DRAG_DROP: 'customDragDrop',
  /** Dock应用添加事件 */
  DOCK_ADD_RECENT_APP: 'dockAddRecentApp',
  /** 网格应用删除事件 */
  GRID_APP_DELETE: 'grid-app-delete'
};

/**
 * 响应状态码枚举
 * 用于API响应状态的管理
 */
export const HTTP_STATUS = {
  /** 成功 */
  SUCCESS: 200,
  /** 创建成功 */
  CREATED: 201,
  /** 无内容 */
  NO_CONTENT: 204,
  /** 错误请求 */
  BAD_REQUEST: 400,
  /** 未授权 */
  UNAUTHORIZED: 401,
  /** 禁止访问 */
  FORBIDDEN: 403,
  /** 未找到 */
  NOT_FOUND: 404,
  /** 服务器错误 */
  INTERNAL_SERVER_ERROR: 500
};

/**
 * 应用尺寸常量
 * 用于统一管理应用图标的尺寸
 */
export const ICON_SIZES = {
  /** 小尺寸图标 */
  SMALL: 24,
  /** 中等尺寸图标 */
  MEDIUM: 40,
  /** 大尺寸图标 */
  LARGE: 60,
  /** 超大尺寸图标 */
  EXTRA_LARGE: 80
};

/**
 * 默认配置常量
 * 用于管理应用的默认设置
 */
export const DEFAULT_CONFIG = {
  /** 最大最近使用应用数量 */
  MAX_RECENT_APPS: 3,
  /** 最大固定应用数量 */
  MAX_PINNED_APPS: 3,
  /** 默认图标颜色 */
  DEFAULT_ICON_COLOR: '#ffffff',
  /** 默认背景颜色 */
  DEFAULT_BG_COLOR: 'white',
  /** 默认透明背景 */
  TRANSPARENT_BG: 'transparent',
  /** 拖拽激活距离阈值 */
  DRAG_ACTIVATION_DISTANCE: 8,
  /** 触摸激活延迟 */
  TOUCH_ACTIVATION_DELAY: 250,
  /** 触摸容差 */
  TOUCH_TOLERANCE: 5
};

/**
 * 颜色预设常量
 * 用于管理应用中的预设颜色选项
 */
export const COLOR_PRESETS = {
  TRANSPARENT: 'transparent',
  WHITE: 'white',
  GRADIENTS: {
    PINK: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
    BLUE: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    MINT: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    ORANGE: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    PURPLE: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    ROSE: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    CYAN: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    GREEN: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    SUNSET: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    DEEP_BLUE: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    RED_PURPLE: 'linear-gradient(135deg, #ff3019 0%, #c90477 100%)',
    PURPLE_PINK: 'linear-gradient(135deg, #7028e4 0%, #e5b2ca 100%)'
  }
};

/**
 * 验证函数
 * 用于验证枚举值的有效性
 */
export const TypeValidators = {
  /**
   * 验证图标类型是否有效
   * @param {*} value 要验证的值
   * @returns {boolean} 是否有效
   */
  isValidIconType(value) {
    return Object.values(PREFERRED_ICON_TYPE).includes(value);
  },

  /**
   * 验证拖拽状态是否有效
   * @param {*} value 要验证的值
   * @returns {boolean} 是否有效
   */
  isValidDragState(value) {
    return Object.values(DRAG_STATE).includes(value);
  },

  /**
   * 验证操作类型是否有效
   * @param {*} value 要验证的值
   * @returns {boolean} 是否有效
   */
  isValidActionType(value) {
    return Object.values(APP_ACTION_TYPE).includes(value);
  }
};

/**
 * 转换函数
 * 用于处理旧版本数据的兼容性转换
 */
export const TypeConverters = {
  /**
   * 转换图标类型值
   * @param {string} value 原始值
   * @param {object} appData 应用数据对象（用于智能推断）
   * @returns {string} 转换后的字符串值
   */
  convertIconType(value, appData = null) {
    // 如果是字符串，进行转换
    if (typeof value === 'string') {
      switch (value.toLowerCase()) {
        case 'ico':
        case '1':
          return PREFERRED_ICON_TYPE.ICO;
        case 'text':
        case '2':
          return PREFERRED_ICON_TYPE.TEXT;
        default:
          // 未知字符串值，根据应用数据智能推断
          if (appData) {
            return this._inferIconType(appData);
          }
          return PREFERRED_ICON_TYPE.TEXT; // 默认使用文本图标
      }
    }
    
    // 如果值为 null、undefined 或无效值，根据应用数据智能推断
    if (appData) {
      return this._inferIconType(appData);
    }
    
    // 无应用数据时返回默认值
    return PREFERRED_ICON_TYPE.TEXT;
  },

  /**
   * 根据应用数据智能推断图标类型
   * @param {object} appData 应用数据对象
   * @returns {string} 推断的图标类型
   * @private
   */
  _inferIconType(appData) {
    // 检查是否有图片图标
    const hasIcoIcon = appData.ico && appData.ico.trim() !== '';
    const hasIconUrl = appData.iconUrl && appData.iconUrl.trim() !== '';
    const hasIcon = appData.icon && appData.icon.trim() !== '';
    
    // 如果有任何形式的图片图标，优先使用 ICO 类型
    if (hasIcoIcon || hasIconUrl || hasIcon) {
      return PREFERRED_ICON_TYPE.ICO;
    }
    
    // 否则使用文本图标
    return PREFERRED_ICON_TYPE.TEXT;
  },

  /**
   * 获取图标类型的描述文字
   * @param {string} value 图标类型值
   * @returns {string} 描述文字
   */
  getIconTypeLabel(value) {
    return PREFERRED_ICON_TYPE_LABELS[value] || '未知类型';
  }
};

// 默认导出所有配置
export default {
  PREFERRED_ICON_TYPE,
  PREFERRED_ICON_TYPE_LABELS,
  DRAG_STATE,
  APP_ACTION_TYPE,
  DATA_TYPE,
  DATA_TYPE_LABELS,
  STORAGE_KEYS,
  EVENT_TYPES,
  HTTP_STATUS,
  ICON_SIZES,
  DEFAULT_CONFIG,
  COLOR_PRESETS,
  TypeValidators,
  TypeConverters
}; 